import React, { forwardRef, useContext } from 'react';
import { shallowEqual } from 'react-redux';

import { RMWContext } from '../../../providers/RMWContext';

import { Tooltip, TooltipPopper } from '@zillow/constellation';
import { Box, Text, TextButton } from '@zillow/constellation-10';

import { useSelector } from '../../../selectors/useSelector';

import accessUtils from '../../../utils/accessUtils';

import { ListingIssuesModalTrigger } from '../../Modal/ListingIssuesModalTrigger';
import { AccordionStateButton } from '../../../containers/PropertyDetailsContainer/components/MFSubHeader/components/AccordionStateHeader/AccordionStateButton';

type Props = {
  title?: string;
  hideListingIssues?: boolean;
};

const ListingHeader = forwardRef<HTMLDivElement, Props>(({ title = '', hideListingIssues = false }: Props, ref) => {
  const { paths } = useContext(RMWContext);
  const { user, active, isFeed, listingAlias, topDupesDetails, cookieEnabledServices, isMultifamily } = useSelector(
    (state) => ({
      user: state.user,
      active: state.properties.currentProperty.active,
      isFeed: state.properties.currentProperty.isFeed,
      listingAlias: state.properties.currentProperty.listingAlias,
      topDupesDetails: state.properties.currentProperty.topDupesDetails,
      cookieEnabledServices: state.app.cookieEnabledServices,
      isMultifamily: state.properties.currentProperty.isMultifamily,
    }),
    shallowEqual,
  );

  const canAccessListingQuality =
    isMultifamily &&
    accessUtils.canAccessListingQuality({
      cookieEnabledServices,
      canAccessZramMultifamilyPropertyTable: user.canAccessZramMultifamilyPropertyTable,
    });

  const canFeedConnectSwitchSource = accessUtils.canFeedConnectSwitchSource({ user });
  const showListingSource = canFeedConnectSwitchSource && !isFeed && topDupesDetails.sourceCount > 1;
  const showListingIssues = canFeedConnectSwitchSource && isFeed && !hideListingIssues && active;
  const showListingIssuesUnderTitle = canAccessListingQuality && showListingIssues;

  const handleGoToListingSource = () => {
    const url = `${paths.propertiesPage}/${listingAlias}/${paths.propertyDetailsSources}`;
    window.router.transitionTo(url);
  };

  const tooltipContent = (
    <Tooltip
      body={
        <Text textStyle="body-xs">
          You have one or more other versions of this listing. Those listings come from other sources, such as feeds,
          and may be active on other sites.
        </Text>
      }
    />
  );

  if (!title && !showListingSource && !showListingIssues && !canAccessListingQuality) {
    return null;
  }

  return (
    <>
      <Box
        css={{
          display: 'flex',
          flexDirection: { base: 'column', md: 'row' },
          alignItems: { base: 'flex-start', md: 'center' },
          paddingBottom: { lg: showListingIssuesUnderTitle ? '0' : '400' },
        }}
        ref={ref}
        data-testid="ListingHeader"
      >
        <Box css={{ flex: '1', paddingBottom: '200' }}>
          <Text textStyle="heading-sm" asChild={true}>
            <p>{title}</p>
          </Text>
        </Box>
        {showListingSource && (
          <Box
            css={{
              marginBottom: canAccessListingQuality ? '0' : '200',
              marginRight: canAccessListingQuality ? '400' : '0',
            }}
          >
            <TextButton onClick={handleGoToListingSource}>Manage listing source</TextButton>
            &nbsp;
            <TooltipPopper triggered={tooltipContent} placement="top" />
          </Box>
        )}
        {Boolean(showListingIssues && !showListingIssuesUnderTitle) && <ListingIssuesModalTrigger />}
        <AccordionStateButton isVisible={canAccessListingQuality} />
      </Box>
      {showListingIssuesUnderTitle && <ListingIssuesModalTrigger showIcon={false} />}
    </>
  );
});

ListingHeader.displayName = 'ListingHeader';

export { ListingHeader };
export default ListingHeader;
